import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import dayjs from 'dayjs'
import { getOperatingCapabilityRatios } from '../../services'
import {
    OperatingCapabilityRatio,
    OperatingCapabilityRatiosParams,
} from '../../types'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import { Sparkles } from 'lucide-react'
import AIAnalysisReport from '../AIAnalysisReport'
import {
    analyzeOperatingCapability,
    GeminiReportResponse,
} from '../../services/gemini'
import {
    getCachedAnalysis,
    generateCacheKey,
    createDataHash,
} from '../../services/aiCache'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts'
import ChartIcons from '@/icons/ChartIcons'
import GridIcons from '@/icons/GridIcons'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { METRIC_COLORS, CHART_THEME } from '@/constants/chartColors'
import RatioFormModal from '@/modals/OperatingCapabilityModals/RatioFormModal'
import BatchInputModal from '@/modals/OperatingCapabilityModals/BatchInputModal'
import AddIcons from '@/icons/AddIcons'
import GokuDataGrid from '@/components/GokuDataGrid'
import { columns } from './columns'
import EditIcons from '@/icons/EditIcons'
import { IAction } from 'vnetwork-ui/dist/components/GokuDataGrid/Actions'

interface OperatingCapabilityTabProps {
    filters?: { gte?: string; lte?: string }
}

type RatioKey =
    | 'total_asset_turnover'
    | 'inventory_turnover'
    | 'receivables_turnover'
    | 'current_asset_turnover'

interface RatioConfig {
    key: RatioKey
    label: string
    color: string
}

const OperatingCapabilityTab: React.FC<OperatingCapabilityTabProps> = ({
    filters,
}) => {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart')
    const [modalOpen, setModalOpen] = useState(false)
    const [batchModalOpen, setBatchModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
    const [editData, setEditData] = useState<OperatingCapabilityRatio | null>(
        null
    )

    // AI Analysis states
    const [aiReport, setAiReport] = useState<GeminiReportResponse | null>(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState<string | null>(null)
    const [aiFromCache, setAiFromCache] = useState(false)
    const [showAiReport, setShowAiReport] = useState(false)

    // Ratios configuration with standardized colors
    const ratios: RatioConfig[] = [
        {
            key: 'total_asset_turnover',
            label: t('total_asset_turnover'),
            color: METRIC_COLORS.total_asset_turnover,
        },
        {
            key: 'inventory_turnover',
            label: t('inventory_turnover'),
            color: METRIC_COLORS.inventory_turnover,
        },
        {
            key: 'receivables_turnover',
            label: t('receivables_turnover'),
            color: METRIC_COLORS.receivables_turnover,
        },
        {
            key: 'current_asset_turnover',
            label: t('current_asset_turnover'),
            color: METRIC_COLORS.current_asset_turnover,
        },
    ]

    const params: OperatingCapabilityRatiosParams = useMemo(
        () => ({
            from_month: filters?.gte,
            to_month: filters?.lte,
        }),
        [filters]
    )

    // Fetch data
    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_BI_OPERATING_CAPABILITY_RATIOS, params],
        queryFn: () => getOperatingCapabilityRatios(params),
        staleTime: 5 * 60 * 1000,
        placeholderData: true,
    })

    // Modal handlers
    const handleCreate = () => {
        setModalMode('create')
        setEditData(null)
        setModalOpen(true)
    }

    const handleEdit = (ratio: OperatingCapabilityRatio) => {
        setModalMode('edit')
        setEditData(ratio)
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        setEditData(null)
    }

    // DataGrid actions
    const dataGridActions: IAction<OperatingCapabilityRatio>[] = [
        {
            onClick: handleEdit,
            title: t('edit'),
            color: 'primary',
            icon: <EditIcons />,
        },
    ]

    // Visible ratios state
    const [visibleRatios, setVisibleRatios] = useState<Set<RatioKey>>(
        new Set(ratios.map((r) => r.key))
    )

    // Auto-load cached AI report when data changes
    useEffect(() => {
        if (!data || data.length === 0) return

        const dataHash = createDataHash(data)
        const cacheKey = generateCacheKey({
            type: 'operating_capability',
            period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
            selected_metrics: Array.from(visibleRatios),
            data_hash: dataHash,
        })

        const cachedReport = getCachedAnalysis(cacheKey)
        if (cachedReport) {
            setAiReport(cachedReport)
            setAiFromCache(true)
            setShowAiReport(true)
            setAiError(null)
        }
    }, [data, filters?.gte, filters?.lte, visibleRatios])

    // Toggle ratio visibility
    const toggleRatio = (key: RatioKey) => {
        setVisibleRatios((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    // Toggle all ratios
    const toggleAll = () => {
        if (visibleRatios.size === ratios.length) {
            setVisibleRatios(new Set())
        } else {
            setVisibleRatios(new Set(ratios.map((r) => r.key)))
        }
    }

    // Prepare chart data with filled missing months
    const chartData = useMemo(() => {
        if (!data || !filters?.gte || !filters?.lte) return []

        // Generate all months in the range
        const allMonths: string[] = []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        let current = start

        while (current.isBefore(end) || current.isSame(end, 'month')) {
            allMonths.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }

        // Create a map of existing data
        const dataMap = new Map(data.map((item) => [item.report_month, item]))

        // Fill all months with data or zeros
        return allMonths.map((month) => {
            const existingData = dataMap.get(month)
            return {
                month,
                total_asset_turnover: existingData?.total_asset_turnover || 0,
                inventory_turnover: existingData?.inventory_turnover || 0,
                receivables_turnover: existingData?.receivables_turnover || 0,
                current_asset_turnover:
                    existingData?.current_asset_turnover || 0,
            }
        })
    }, [data, filters?.gte, filters?.lte])

    // Format number for display
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('vi-VN', {
            maximumFractionDigits: 2,
        }).format(num)
    }

    // Format month for display
    const formatMonth = (monthStr: string | number | undefined) => {
        if (!monthStr) return ''
        const str = String(monthStr)
        const [year, month] = str.split('-')
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${monthNames[parseInt(month) - 1]} ${year}`
    }

    // Handle AI Analysis
    const handleAIAnalysis = async () => {
        if (!data || data.length === 0) {
            setAiError('Không có dữ liệu để phân tích')
            setShowAiReport(true)
            return
        }

        setShowAiReport(true)
        setAiLoading(true)
        setAiError(null)
        setAiReport(null)
        setAiFromCache(false)

        try {
            const dataHash = createDataHash(data)
            const cacheKey = generateCacheKey({
                type: 'operating_capability',
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleRatios),
                data_hash: dataHash,
            })

            const cachedReport = getCachedAnalysis(cacheKey)
            if (cachedReport) {
                setAiReport(cachedReport)
                setAiFromCache(true)
                setAiLoading(false)
                return
            }

            const report = await analyzeOperatingCapability({
                data,
                period_range: `${filters?.gte || 'N/A'} đến ${filters?.lte || 'N/A'}`,
                selected_metrics: Array.from(visibleRatios),
            })
            setAiReport(report)
            setAiFromCache(false)
        } catch (error: any) {
            console.error('AI Analysis Error:', error)
            setAiError(
                error?.message ||
                    'Không thể kết nối với dịch vụ AI. Vui lòng thử lại sau.'
            )
        } finally {
            setAiLoading(false)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {/* Header with filters and view toggle */}
            <div className="bg-white border border-gray-200 rounded-md p-4">
                <div className="flex items-center justify-end mb-2">
                    <div className="flex gap-2">
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleAIAnalysis}
                            className="flex items-center gap-2"
                            disabled={!data || data.length === 0}
                        >
                            <Sparkles className="w-4 h-4" />
                            {t('analyze_with_ai')}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<GridIcons className="w-4 h-4" />}
                            onClick={() => setBatchModalOpen(true)}
                        >
                            {t('batch_input')}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcons />}
                            onClick={handleCreate}
                        >
                            {t('create')}
                        </Button>
                        <IconButton
                            size="small"
                            color={viewMode === 'chart' ? 'primary' : 'default'}
                            onClick={() => setViewMode('chart')}
                            className="border text-primary"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={viewMode === 'table' ? 'primary' : 'default'}
                            onClick={() => setViewMode('table')}
                            className="border text-primary"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Ratio Filters */}
                <div className="flex flex-wrap gap-4 items-center">
                    {/* Select All */}
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="select-all"
                            checked={visibleRatios.size === ratios.length}
                            onCheckedChange={toggleAll}
                        />
                        <Label
                            htmlFor="select-all"
                            className="text-sm cursor-pointer"
                        >
                            {t('select_all')}
                        </Label>
                    </div>

                    {/* Individual Ratios */}
                    {ratios.map((ratio) => (
                        <div
                            key={ratio.key}
                            className="flex items-center gap-2"
                        >
                            <Checkbox
                                id={ratio.key}
                                checked={visibleRatios.has(ratio.key)}
                                onCheckedChange={() => toggleRatio(ratio.key)}
                                style={
                                    visibleRatios.has(ratio.key)
                                        ? {
                                              backgroundColor: ratio.color,
                                              borderColor: ratio.color,
                                          }
                                        : {
                                              borderColor: ratio.color,
                                          }
                                }
                            />
                            <Label
                                htmlFor={ratio.key}
                                className="text-sm cursor-pointer"
                                style={{
                                    color: visibleRatios.has(ratio.key)
                                        ? ratio.color
                                        : undefined,
                                }}
                            >
                                {ratio.label}
                            </Label>
                        </div>
                    ))}
                </div>
            </div>

            {/* Chart View - Individual Charts for Each Ratio */}
            {viewMode === 'chart' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {ratios.map((ratio) =>
                        visibleRatios.has(ratio.key) ? (
                            <div
                                key={ratio.key}
                                className="bg-white border border-gray-200 rounded-lg p-6"
                            >
                                <Typogaphy
                                    className="font-medium text-base mb-4"
                                    style={{ color: ratio.color }}
                                >
                                    {ratio.label}
                                </Typogaphy>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={chartData}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid
                                            stroke={CHART_THEME.grid.stroke}
                                            strokeDasharray={
                                                CHART_THEME.grid.strokeDasharray
                                            }
                                        />
                                        <XAxis
                                            dataKey="month"
                                            tickFormatter={formatMonth}
                                            tick={{
                                                fontSize:
                                                    CHART_THEME.axis.fontSize,
                                                fontFamily:
                                                    CHART_THEME.axis.fontFamily,
                                            }}
                                            stroke={CHART_THEME.axis.stroke}
                                        />
                                        <YAxis
                                            tickFormatter={(value) =>
                                                `${formatNumber(value)}x`
                                            }
                                            tick={{
                                                fontSize:
                                                    CHART_THEME.axis.fontSize,
                                                fontFamily:
                                                    CHART_THEME.axis.fontFamily,
                                            }}
                                            stroke={CHART_THEME.axis.stroke}
                                        />
                                        <Tooltip
                                            contentStyle={
                                                CHART_THEME.tooltip.contentStyle
                                            }
                                            itemStyle={
                                                CHART_THEME.tooltip.itemStyle
                                            }
                                            labelStyle={
                                                CHART_THEME.tooltip.labelStyle
                                            }
                                            formatter={(value: number) =>
                                                `${formatNumber(value)}x`
                                            }
                                            labelFormatter={formatMonth}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey={ratio.key}
                                            name={ratio.label}
                                            stroke={ratio.color}
                                            strokeWidth={1}
                                            dot={{ r: 2, fill: ratio.color }}
                                            activeDot={{ r: 2 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : null
                    )}
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <GokuDataGrid
                        apiCall={async () => ({
                            results: data || [],
                            total: data?.length || 0,
                        })}
                        columns={columns(t)}
                        actions={dataGridActions}
                        dataGridProps={{
                            getRowId: (row) => row.id,
                            hideFooter: true,
                            hideFooterPagination: true,
                        }}
                        options={{
                            pageSize: 50,
                            showSearch: false,
                            title: undefined,
                        }}
                        reactQueryKey={
                            `${QUERY_KEYS.REPORTS_BI_OPERATING_CAPABILITY_RATIOS}` +
                            JSON.stringify(params)
                        }
                    />
                </div>
            )}

            {/* AI Analysis Report */}
            {showAiReport && (
                <AIAnalysisReport
                    report={aiReport}
                    loading={aiLoading}
                    error={aiError}
                    fromCache={aiFromCache}
                />
            )}

            {/* CRUD Modal */}
            <RatioFormModal
                open={modalOpen}
                onClose={handleCloseModal}
                mode={modalMode}
                editData={editData}
                refetch={refetch}
            />

            {/* Batch Input Modal */}
            <BatchInputModal
                open={batchModalOpen}
                onClose={() => setBatchModalOpen(false)}
                refetch={refetch}
            />
        </div>
    )
}

export default OperatingCapabilityTab
